<template>
    <div class="row">
        <div class="field col-6 mb-3">
            <label>{{ t('plans-form-billing_type') }}</label>
            <select class="form-control" v-model="form.billing_type">
                <option value="prepaid">{{ t('plans-form-prepaid') }}</option>
                <option value="postpaid">{{ t('plans-form-postpaid') }}</option>
            </select>
        </div>
        <div class="field col-6 mb-3">
            <label>{{ t('plans-form-currency') }}</label>
            <select class="form-control" v-model="form.currency">
                <option value="BRL">{{ t('plans-form-currency_brl') }}</option>
                <option value="USD">{{ t('plans-form-currency_usd') }}</option>
            </select>
        </div>
        <div class="field col-12 mb-3" v-if="form.billing_type === 'postpaid'">
            <label class="d-flex align-items-center mb-1" style="cursor: pointer;">
                <input
                    type="checkbox"
                    v-model="form.one_invoice_per_branch_plus_base"
                    style="margin: 0 8px 0 0; cursor: pointer; width: 16px; height: 16px; flex-shrink: 0;"
                />
                <span>{{ t('plans-form-one_invoice_per_branch_plus_base') }}</span>
            </label>
            <small class="form-text text-muted" style="margin-top: 0; margin-left: 24px;">
                {{ t('plans-form-one_invoice_per_branch_plus_base_help') }}
            </small>
        </div>
        <div
            class="field col-12 mb-3"
            v-if="form.billing_type === 'postpaid' && form.one_invoice_per_branch_plus_base"
        >
            <label>{{ t('plans-form-main_branch') }}</label>
            <select class="form-control" v-model="form.main_branch_id">
                <option :value="null">{{ t('plans-form-select_main_branch') }}</option>
                <option
                    v-for="branch in activeBranches"
                    :key="branch.id"
                    :value="branch.id"
                >
                    {{ branch.title }}
                </option>
            </select>
            <small class="form-text text-muted">
                {{ t('plans-form-main_branch_help') }}
            </small>
        </div>
        <div class="field col-6 mb-3">
            <label>{{ t('plans-form-base_value') }}</label>
            <input class="form-control" type="number" v-model="form.base_fee" />
        </div>
        <div class="field col-6 mb-3" v-if="form.billing_type === 'postpaid'">
            <label>{{ t('plans-form-incentivized_revenue_share') }}</label>
            <input
                class="form-control"
                type="number"
                v-model="form.incentivized_revenue_share"
            />
        </div>
        <div class="field col-6 mb-3" v-if="form.billing_type === 'postpaid'">
            <label>{{ t('plans-form-retention_revenue_share') }}</label>
            <input
                class="form-control"
                type="number"
                v-model="form.retention_revenue_share"
            />
        </div>
        <div class="field col-12 mb-3">
            <label>{{ t('plans-form-pricetable_clients') }}</label>
            <PriceTable v-model="form.pricetable_clients" :currency="form.currency" />
        </div>
        <div class="field col-12 mb-3">
            <label>{{ t('plans-form-pricetable_credits') }}</label>
            <PriceTable v-model="form.pricetable_credits" :currency="form.currency" />
        </div>
        <div class="field col-12 mb-3" v-if="form.billing_type === 'prepaid'">
            <label>{{ t('plans-form-pricetable_extra_credits') }}</label>
            <PriceTable v-model="form.pricetable_extra_credits" :currency="form.currency" />
        </div>
        <div class="field col-6 mb-3">
            <label>{{ t('plans-form-included_integrations') }}</label>
            <input
                class="form-control"
                type="number"
                v-model="form.included_integrations"
            />
        </div>
        <div class="field col-6 mb-3">
            <label>{{ t('plans-form-price_per_integration') }}</label>
            <input
                class="form-control"
                type="number"
                v-model="form.price_per_integration"
            />
        </div>
        <div class="field col-6 mb-3">
            <label>{{ t('plans-form-included_branches') }}</label>
            <input class="form-control" type="text" v-model="form.included_branches" />
        </div>
        <div class="field col-6 mb-3">
            <label>{{ t('plans-form-price_per_branch') }}</label>
            <input class="form-control" type="text" v-model="form.price_per_branch" />
        </div>
        <div class="field col-6 mb-3">
            <label>{{ t('plans-form-included_users') }}</label>
            <input class="form-control" type="text" v-model="form.included_users" />
        </div>
        <div class="field col-6 mb-3">
            <label>{{ t('plans-form-price_per_user') }}</label>
            <input class="form-control" type="text" v-model="form.price_per_user" />
        </div>

        <div class="field col-6 mb-3">
            <label>{{ t('plans-form-included_users_per_branch') }}</label>
            <input class="form-control" type="text" v-model="form.included_users_per_branch" />
        </div>

        <div class="field col-12 mb-3 mt-4">
            <h5 class="m-0 font-weight-500" style="font-size: 1.1rem; margin-bottom: 10px !important;">{{ t('plans-form-readjustment-title') }}</h5>
            <label>{{ t('plans-form-readjustment-percentage') }}</label>
            <div class="input-group">
                <input type="number" step="0.01" class="form-control" v-model="readjustment_percentage" :placeholder="t('plans-form-readjustment-placeholder')" />
                <div class="input-group-append">
                    <button class="btn btn-primary" @click.prevent="applyReadjustment">
                        {{ t('plans-form-readjustment-apply') }}
                    </button>
                </div>
            </div>
            <small class="form-text text-muted mt-2">{{ t('plans-form-readjustment-help') }}</small>
        </div>

        <div class="field col-12 mb-3">
            <label>{{ t('plans-form-change_reason-label') }}</label>
            <input
                class="form-control"
                type="text"
                v-model="form.change_reason"
                :placeholder="t('plans-form-change_reason-placeholder')"
                @invalid.prevent="setCustomValidity($event, 'places-form-validation-change_reason_required')"
            />
            <span v-if="hasChanges && !form.change_reason" class="error">{{ t('plans-form-change_reason-required_alert') }}</span>
        </div>

        <DiscountTable v-model="form.discounts" :currency="form.currency" />
    </div>
</template>

<script>
import { mapState } from "vuex";
import PriceTable from "../plans/priceTable.vue";
import DiscountTable from "../plans/discountTable.vue";

const emptyForm = () => ({
    plans_id: null,
    currency: "BRL",
    billing_type: null,
    base_fee: null,
    incentivized_revenue_share: null,
    retention_revenue_share: null,
    pricetable_clients: [],
    pricetable_credits: [],
    pricetable_extra_credits: [],
    included_integrations: null,
    price_per_integration: null,
    included_branches: null,
    price_per_branch: null,
    included_users: null,
    price_per_user: null,
    included_users_per_branch: null,
    one_invoice_per_branch_plus_base: false,
    main_branch_id: null,
    discounts: [],
    change_reason: null,
});

export default {
    components: { PriceTable, DiscountTable },
    props: {
        selectedPlan: {
            type: Object,
            default: null,
        },
        currentValues: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            readjustment_percentage: null,
            form: emptyForm(),
            initialSnapshot: null,
            errors: {change_reason: null},
        };
    },
    created() {
        this.initForm();
    },
    watch: {
        selectedPlan(newVal, oldVal) {
            if (newVal && oldVal && newVal.id !== oldVal.id) {
                this.loadTemplate(newVal);
            } else if (newVal && !oldVal && !this.currentValues) {
                this.loadTemplate(newVal);
            }
        },
        form: {
            deep: true,
            handler(val) {
                this.$emit("change", val);
            },
        },
    },
    computed: {
        ...mapState({
            branches: (state) => state.placeDetails.branches,
        }),
        activeBranches() {
            return (this.branches || []).filter((branch) => Number(branch.active) === 1);
        },
        hasChanges() {
            if (!this.initialSnapshot) return false;
            const initial = JSON.stringify({ ...this.form, change_reason: null });
            const current = JSON.stringify({ ...this.initialSnapshot, change_reason: null });
            return initial !== current;
        }
    },
    methods: {
        initForm() {
            if (this.currentValues) {
                this.loadSaved(this.currentValues);
            } else if (this.selectedPlan) {
                this.loadTemplate(this.selectedPlan);
            } else {
                this.form = emptyForm();
                this.$emit("change", this.form);
            }
        },
        loadSaved(saved) {
            this.form = {
                ...this.form,
                plans_id: saved.plans_id,
                currency: saved.currency || "BRL",
                billing_type: saved.billing_type,
                base_fee: saved.base_fee,
                incentivized_revenue_share: saved.incentivized_revenue_share,
                retention_revenue_share: saved.retention_revenue_share,
                pricetable_clients: saved.pricetable_clients ?? [],
                pricetable_credits: saved.pricetable_credits ?? [],
                pricetable_extra_credits: saved.pricetable_extra_credits ?? [],
                included_integrations: saved.included_integrations,
                price_per_integration: saved.price_per_integration,
                included_branches: saved.included_branches,
                price_per_branch: saved.price_per_branch,
                included_users: saved.included_users,
                price_per_user: saved.price_per_user,
                included_users_per_branch: saved.included_users_per_branch,
                one_invoice_per_branch_plus_base: Number(saved.one_invoice_per_branch_plus_base) === 1,
                main_branch_id: saved.main_branch_id ? saved.main_branch_id : null,
                discounts: saved.discounts ?? [],
                change_reason: null,
            };
            this.$emit("change", this.form);
            this.initialSnapshot = JSON.parse(JSON.stringify(this.form));
        },
        loadTemplate(plan) {
            this.form = {
                plans_id: plan.id,
                currency: plan.currency || "BRL",
                billing_type: plan.billing_type,
                base_fee: plan.base_fee,
                incentivized_revenue_share: plan.incentivized_revenue_share,
                retention_revenue_share: plan.retention_revenue_share,
                pricetable_clients: plan.pricetable_clients ?? [],
                pricetable_credits: plan.pricetable_credits ?? [],
                pricetable_extra_credits: plan.pricetable_extra_credits ?? [],
                included_integrations: plan.included_integrations,
                price_per_integration: plan.price_per_integration,
                included_branches: plan.included_branches,
                price_per_branch: plan.price_per_branch,
                included_users: plan.included_users,
                price_per_user: plan.price_per_user,
                included_users_per_branch: plan.included_users_per_branch,
                one_invoice_per_branch_plus_base: Number(plan.one_invoice_per_branch_plus_base) === 1,
                main_branch_id: this.form.main_branch_id || null,
                discounts: plan.discounts ?? [],
                change_reason: null,
            };
            this.$emit("change", this.form);
            this.initialSnapshot = JSON.parse(JSON.stringify(this.form));
        },
        applyReadjustment() {
            const percentage = parseFloat(this.readjustment_percentage);
            if (isNaN(percentage) || percentage === 0) return;
            
            const multiplier = 1 + (percentage / 100);

            const adjustValue = (val) => {
                if (val !== null && val !== "" && !isNaN(val)) {
                    return parseFloat((parseFloat(val) * multiplier).toFixed(2));
                }
                return val;
            };

            this.form.base_fee = adjustValue(this.form.base_fee);
            this.form.price_per_integration = adjustValue(this.form.price_per_integration);
            this.form.price_per_branch = adjustValue(this.form.price_per_branch);
            this.form.price_per_user = adjustValue(this.form.price_per_user);

            const adjustTable = (table) => {
                if (table && table.length > 0) {
                    return table.map(row => ({
                        ...row,
                        unitValue: adjustValue(row.unitValue)
                    }));
                }
                return table;
            };

            this.form.pricetable_clients = adjustTable(this.form.pricetable_clients);
            this.form.pricetable_credits = adjustTable(this.form.pricetable_credits);
            this.form.pricetable_extra_credits = adjustTable(this.form.pricetable_extra_credits);

            this.$store.dispatch(
                "notification/success",
                this.t("plans-form-readjustment-success", { percentage })
            );
        },
        setCustomValidity(event, key) {
            event.target.setCustomValidity(this.t(key));
        },
        t(term, replaces) {
            return this.$root.t ? this.$root.t(term, replaces) : term;
        },
    }
};
</script>
